/**
 * EduSense 네비게이션을 ui-layout NavigationItem 인터페이스로 구현
 */
import React, { useMemo, useLayoutEffect } from 'react';
import type { NavigationItem } from '@edusense/shared-ui-layout';
import { useLocation } from 'react-router';
// Split imports to avoid Rollup binding analysis issues
import { nav_home } from '@paraglide/messages';
import { nav_courses } from '@paraglide/messages';
import { course_courses_types_roadmaps } from '@paraglide/messages';
import { nav_about } from '@paraglide/messages';
import { nav_notifications } from '@paraglide/messages';
import { nav_settings } from '@paraglide/messages';
import { common_profile } from '@paraglide/messages';
// Fixed import to use main lucide-react package to resolve TDZ initialization issues
import { Rss, BookOpen, Compass, Bell, Settings, User, MapPin } from 'lucide-react';
import { useUILayoutAuth } from './AuthProviderImpl';

/**
 * EduSense 네비게이션 아이템들을 생성하는 훅
 * 사용자 인증 상태에 따라 동적으로 네비게이션 아이템을 생성
 */
export const useUILayoutNavigation = (): NavigationItem[] => {
  const location = useLocation();
  const auth = useUILayoutAuth();

  // 🎯 우아한 React 방식: useLayoutEffect로 동기적 디버깅
  useLayoutEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 [NavigationProvider] Location updated:', {
        pathname: location.pathname,
        key: location.key,
        state: location.state
      });
    }
  }, [location.pathname, location.key, location.state]);

  // 🚀 React Router location 신뢰하며 useMemo로 최적화
  const navigationItems = useMemo(() => {
    // 언어 prefix 제거한 경로 (활성화 상태 비교용)
    const pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/';
    
    // 🔍 개발 환경에서만 디버깅 로그
    if (process.env.NODE_ENV === 'development') {
      console.log('✨ [NavigationProvider] Computing menu states:', {
        'Raw pathname': location.pathname,
        'Path without lang': pathWithoutLang,
        'Location key': location.key
      });
    }
    
    // 각 메뉴 활성화 상태 계산
    const homeActive = pathWithoutLang === '/' || pathWithoutLang === '/feed';
    const coursesActive = pathWithoutLang.includes('/learn') && !pathWithoutLang.includes('/roadmap');
    const roadmapsActive = pathWithoutLang.includes('/roadmap');
    const discoverActive = pathWithoutLang.includes('/discover'); 
    const notificationsActive = pathWithoutLang.includes('/notifications');
    const settingsActive = pathWithoutLang.includes('/settings');
    const profileActive = pathWithoutLang.includes('/profile');
    
    // 🔍 개발 환경에서만 메뉴 상태 디버깅
    if (process.env.NODE_ENV === 'development') {
      console.log('📋 [NavigationProvider] Menu Active States:', {
        'Path for comparison': pathWithoutLang,
        'home': homeActive,
        'courses': coursesActive,
        'discover': discoverActive,  
        'notifications': notificationsActive,
        'settings': settingsActive,
        'profile': profileActive
      });
    }

    // 기본 네비게이션 아이템들 - 5개로 간소화
    const items: NavigationItem[] = [
      {
        id: 'home',
        label: nav_home(),
        icon: React.createElement(Rss, { size: 20 }),
        href: '/feed',
        isActive: homeActive,
        mobileNavBarVisible: true,
      },
      {
        id: 'courses',
        label: nav_courses(),
        icon: React.createElement(BookOpen, { size: 20 }),
        href: '/learn',
        isActive: coursesActive,
        mobileNavBarVisible: true,
      },
      {
        id: 'roadmaps',
        label: course_courses_types_roadmaps(),
        icon: React.createElement(MapPin, { size: 20 }),
        href: '/learn/roadmap/new',
        isActive: roadmapsActive,
        mobileNavBarVisible: true,
      },
      {
        id: 'about',
        label: nav_about(),
        icon: React.createElement(Compass, { size: 20 }),
        href: '/discover',
        isActive: discoverActive,
        mobileNavBarVisible: true,
      },
      {
        id: 'notifications',
        label: nav_notifications(),
        icon: React.createElement(Bell, { size: 20 }),
        href: '/notifications',
        isActive: notificationsActive,
        mobileNavBarVisible: true,
      },
      {
        id: 'settings',
        label: nav_settings(),
        icon: React.createElement(Settings, { size: 20 }),
        href: '/settings',
        isActive: settingsActive,
        mobileNavBarVisible: false,
      },
      {
        id: 'profile',
        label: common_profile(),
        icon: React.createElement(User, { size: 20 }),
        href: '/profile',
        isActive: profileActive,
        mobileNavBarVisible: false,
        // 인증된 사용자만 표시
        visible: auth.isAuthenticated,
      },
    ];
    
    console.log('✨ [NavigationProvider] Generated navigation items:', 
      items.map(item => ({ id: item.id, isActive: item.isActive, href: item.href }))
    );
    
    return items;
  }, [location.pathname, location.key, auth.isAuthenticated]); // 의존성 배열 명시적 관리
  
  return navigationItems;
};
